import React, { useState, useEffect } from "react";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useHttpClient } from "../../hooks/http-hook";
import { useParams } from "react-router-dom";
import SectionItem from "./SectionItem";
import { Button } from "react-bootstrap";
import { debounce } from "lodash";
import { useModuleContext } from "../../Context/ModuleContext";
import "./ModuleTreeStructure.css";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const ModuleTreeStructure = () => {
  const { t } = useTranslation(); // Assigner useTranslation à {t}
  const { sendRequest } = useHttpClient();
  const [sections, setSections] = useState([]);
  const { moduleId } = useParams();
  const sensors = useSensors(useSensor(PointerSensor), useSensor(TouchSensor));
  const { module, refreshModule } = useModuleContext();
  const [isMoving, setIsMoving] = useState(false);

  useEffect(() => {
    if (module) {
      setSections(module.sections);
    }
  }, [module]);

  const handleAddSection = async () => {
    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}sections`,
        "POST",
        JSON.stringify({ title: t("new_section"), module: moduleId }),
        {
          "Content-Type": "application/json",
        }
      );

      const newSection = response.section;
      setSections((prevSections) => [
        ...prevSections,
        {
          id: newSection._id,
          title: newSection.title,
          lessons: [],
          is_visible: newSection.is_visible,
        },
      ]);
      refreshModule();
    } catch (err) {
      console.error(t("add_section_error"), err);
    }
  };

  const handleAddLessonToSection = (sectionId, newLesson) => {
    setSections((prevSections) =>
      prevSections.map((section) =>
        section.id === sectionId
          ? {
              ...section,
              lessons: [...section.lessons, newLesson],
            }
          : section
      )
    );
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id || isMoving) return;

    const prevSections = [...sections];
    const lesson = findLessonById(prevSections, active.id);

    if (lesson) {
      setIsMoving(true);

      try {
        const sourceSection = findSectionByLessonId(prevSections, active.id);
        const targetSection =
          findSectionByLessonId(prevSections, over.id) ||
          findSectionById(prevSections, over.id);

        if (sourceSection && targetSection) {
          const newSections = [...prevSections];
          const newSourceSection = newSections.find(
            (s) => s.id === sourceSection.id
          );
          const newTargetSection = newSections.find(
            (s) => s.id === targetSection.id
          );

          let newPos = 0;
          if (newTargetSection.lessons.length > 0) {
            const overLessonIndex = newTargetSection.lessons.findIndex(
              (l) => l.id === over.id
            );
            newPos =
              overLessonIndex !== -1
                ? overLessonIndex
                : newTargetSection.lessons.length;
          }

          newSourceSection.lessons = newSourceSection.lessons.filter(
            (l) => l.id !== active.id
          );
          newTargetSection.lessons.splice(newPos, 0, lesson);
          setSections(newSections);

          const response = await sendRequest(
            `${process.env.REACT_APP_BACKEND_URL}lessons/move`,
            "PATCH",
            JSON.stringify({
              lessonId: lesson.id,
              fromSectionId: sourceSection.id,
              toSectionId: targetSection.id,
              newPos,
            }),
            { "Content-Type": "application/json" }
          );

          if (response.updatedSourceSection && response.updatedTargetSection) {
            setSections((prevSections) => {
              return prevSections.map((section) => {
                if (section.id === sourceSection.id) {
                  return response.updatedSourceSection;
                }
                if (section.id === targetSection.id) {
                  return response.updatedTargetSection;
                }
                return section;
              });
            });
          }
        }
      } catch (err) {
        console.error(t("move_lesson_error"), err);
        setSections(prevSections);
      } finally {
        setIsMoving(false);
      }
    } else {
      setIsMoving(true);

      try {
        const newSections = [...prevSections];
        const oldIndex = newSections.findIndex((s) => s.id === active.id);
        const newIndex = newSections.findIndex((s) => s.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const updatedSections = arrayMove(newSections, oldIndex, newIndex);

          setSections(updatedSections);

          const sectionIds = updatedSections.map((section) => section.id);

          const response = await sendRequest(
            `${process.env.REACT_APP_BACKEND_URL}modules/${moduleId}`,
            "PATCH",
            JSON.stringify({ sections: sectionIds }),
            { "Content-Type": "application/json" }
          );

          console.log(t("move_section_success"), response);
        }
      } catch (err) {
        console.error(t("move_section_error"), err);
        setSections(prevSections);
      } finally {
        setIsMoving(false);
      }
    }
  };

  const findLessonById = (sections, id) =>
    sections.flatMap((s) => s.lessons).find((l) => l.id === id);

  const findSectionByLessonId = (sections, lessonId) =>
    sections.find((s) => s.lessons.some((l) => l.id === lessonId));

  const findSectionById = (sections, id) => sections.find((s) => s.id === id);

  const debouncedSaveSectionsOrder = debounce((sections) => {
    saveSectionsOrder(sections);
  }, 1000);

  const saveSectionsOrder = async (sections) => {
    try {
      const sectionIds = sections.map((section) => section.id);

      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}modules/${moduleId}`,
        "PATCH",
        JSON.stringify({ sections: sectionIds }),
        {
          "Content-Type": "application/json",
        }
      );

      if (!response.ok) {
        throw new Error(t("save_sections_order_error"));
      }

      console.log(t("sections_order_saved"), response);
    } catch (err) {
      console.error(t("save_sections_order_error"), err);
    }
  };

  return (
    <div className="module-container">
      <div className="d-flex justify-content-end m-2">
        <Button variant="primary" onClick={handleAddSection}>
          {t("add_section")}
        </Button>
      </div>
      <div
        className="module-scrollable-content"
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
        }}
      >
        <DndContext
          sensors={sensors}
          onDragEnd={handleDragEnd}
          collisionDetection={closestCorners}
        >
          <SortableContext
            items={sections}
            strategy={verticalListSortingStrategy}
          >
            {sections.map((section) => (
              <SectionItem
                key={section.id}
                section={section}
                onAddLesson={handleAddLessonToSection}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};

export default ModuleTreeStructure;